import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import NewDraftModal from "@/components/review/NewDraftModal";
import DraftsList from "@/components/review/DraftsList";
import PageHeader from "@/components/shell/PageHeader";
import Button from "@/components/ui/Button";
import { Page, PageBody } from "@/components/ui/Page";
import { TabList, tabProps } from "@/components/ui/Tabs";
import { trpc } from "@/lib/trpc";
import { Route as OrgRoute } from "./$orgSlug";

export const Route = createFileRoute("/_authenticated/$orgSlug/review/")({
  // Same cache-sharing pattern as $orgSlug.index.tsx: the loader ensures the
  // entry that DraftsList's useSuspenseQuery reads.
  loader: ({ params, context }) =>
    context.queryClient.ensureQueryData(
      trpc.review.listDrafts.queryOptions({ orgSlug: params.orgSlug }),
    ),
  component: ReviewDraftsPage,
});

function ReviewDraftsPage() {
  const { orgSlug } = Route.useParams();
  const { currentOrg } = OrgRoute.useLoaderData();
  const navigate = useNavigate();
  const [showNewDraft, setShowNewDraft] = useState(false);
  const [tab, setTab] = useState<"active" | "archived">("active");

  function openDraft(draftId: string) {
    setShowNewDraft(false);
    void navigate({
      to: "/$orgSlug/review/$draftId",
      params: { orgSlug, draftId },
    });
  }

  return (
    <Page>
      <PageHeader
        title="Review"
        actions={
          <Button variant="primary" icon={<Plus size={14} />} onClick={() => setShowNewDraft(true)}>
            New review
          </Button>
        }
        tabs={
          <TabList aria-label="Review status">
            {(["active", "archived"] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={tab === value}
                onClick={() => setTab(value)}
                {...tabProps(tab === value)}
              >
                {value.charAt(0).toUpperCase() + value.slice(1)}
              </button>
            ))}
          </TabList>
        }
      />
      <PageBody>
        <DraftsList orgSlug={orgSlug} tab={tab} canDelete={currentOrg.role === "admin"} />
      </PageBody>
      {showNewDraft && (
        <NewDraftModal
          orgSlug={orgSlug}
          onClose={() => setShowNewDraft(false)}
          onCreated={openDraft}
        />
      )}
    </Page>
  );
}
